
import React from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import "katex/dist/katex.min.css";
import { BlockMath } from "react-katex";
import PointsChart from '../components/PointsChart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

export default function BasicDemo() {
    return (
        <div className="md:col-6 md:col-offset-3 col-12 col-offset-0">
            <h1>F.A.Q.</h1>
            <div className="card">
                <Accordion activeIndex={0}>
                    <AccordionTab header="How are GeoRank points calculated for players?">
                        <p>
                            The points system rewards players based on their global ranking in different ranking types and game modes. 
                            The formula ensures that higher-ranked players receive more points, with a smooth curve that gradually reduces points as rank increases.
                        </p>
                        <BlockMath>
                            {"\\text{Points} = \\newline 1000 \\times \\left(1 - \\frac{\\log_{10}\\left(1 + \\frac{\\text{Rank} - 1}{3} \\right)}{\\log_{10}\\left(1 + \\frac{\\text{Max Rank} - 1}{3} \\right)}\\right)"}
                        </BlockMath>
                        <ul>
                            <li><b>Rank</b>: Player's global rank</li>
                            <li><b>Max Rank</b>: The highest rank eligible for points, varying by ranking type and game mode</li>
                            <li>The <b>steepness factor</b> (set to 3) controls how quickly points decrease as rank increases</li>
                        </ul>
                        <p>Players beyond the <b>Max Rank</b> threshold receive <b>0 points</b>.</p>
                        <p>
                            <PointsChart />
                        </p>
                    </AccordionTab>
                    <AccordionTab header="What are the rankings based on?">
                        <p className="m-0">
                            All the rankings are based on <a href="https://www.geoguessr.com/multiplayer"><strong>GeoGuessr's competetive modes</strong></a> (Duels and Team Duels).
                        </p>
                        <p className="m-0">
                            You can read how GeoGuessr's competetive mode and their ratings work <a href='https://www.geoguessr.com/multiplayer/how-it-works'><strong>here</strong></a>.
                        </p>
                    </AccordionTab>
                    <AccordionTab header="How often does the rating change?">
                        <p className="m-0">
                            Ratings are being pulled and recalculated <strong>daily</strong> at around 9pm CET/CEST time.
                        </p>
                    </AccordionTab>
                    <AccordionTab header="What are the different ranking types (Regular, Pro, Elite)?">
                        <p>Players belong to different <b>ranking types</b>, which determine how many players are eligible for points:</p>
                        <ul>
                            <li><b>Elite Ranking</b> → Points are only awarded to the <b>Top 30</b> players</li>
                            <li><b>Pro Ranking</b> → Points are only awarded to the <b>Top 100</b> players</li>
                            <li><b>Regular Ranking</b> → Points vary based on <b>game mode</b>, generally ~25% of ranked players (explained below)</li>
                        </ul>                        

                        <DataTable rowGroupMode="rowspan" groupRowsBy="rankingType" value={[
                            {rankingType: 'Elite', mode: 'All modes', maxRank: '30'},
                            {rankingType: 'Pro', mode: 'All modes', maxRank: '100'},
                            { rankingType: 'regular', mode: 'Overall', maxRank: 5000 },
                            { rankingType: 'regular', mode: 'Moving', maxRank: 2500 },
                            { rankingType: 'regular', mode: 'No Move', maxRank: 3000 },
                            { rankingType: 'regular', mode: 'NMPZ', maxRank: 500 }
                        ]} stripedRows>
                            <Column field="rankingType" header="Ranking type"></Column>
                            <Column field="mode" header="Game Mode"></Column>
                            <Column field="maxRank" header="Number of players awarded"></Column>
                        </DataTable>
                    </AccordionTab>

                    <AccordionTab header="How do country rankings work?">
                        <p>
                            <b>Country rankings</b> are determined by aggregating the <b>points of the top players from each country</b>. 
                            The ranking reflects the overall strength of a country’s top-performing players.
                        </p>
                        <p>
                            For each country, we sum the points of the <b>best-performing players</b> based on the ranking type. 
                            This ensures a fair comparison between countries of different sizes.
                        </p>
                    </AccordionTab>

                    <AccordionTab header="Terminology explained (Rating, GeoRank Points, Game Modes, Ranking Types etc.)">
                        <p>
                            The ranking system used in this platform is designed to measure and compare player performance based on various criteria. Below is a summary of the key terms:
                        </p>

                        <h3>1. Rating</h3>
                        <p>
                            <i>Rating</i> is the numerical score assigned to a player <strong>by GeoGuessr</strong> based on their performance in a specific game mode. It represents the player's skill level, with higher ratings indicating better performance. The rating is calculated using factors like game results, position, and the strength of opponents.
                        </p>

                        <h3>2. Rank or position</h3>
                        <p>
                            <i>Rank</i> refers to a player's position in a leaderboard or ranking system. It is directly tied to their rating. For example, the top-ranked player has the highest rating in a given ranking type, while the rank number indicates where a player stands relative to others.
                        </p>

                        <h3>3. Ranking and Ranking Types</h3>
                        <p>
                            <i>Ranking</i> is the system that organizes players based on their ratings. It provides a way to compare players against each other within a particular game mode, country, or globally. The <i>ranking</i> can be categorized into different types such as "elite," "pro," or "regular," depending on the performance level or game mode of a player.
                        </p>

                        <h3>4. GeoRank Points</h3>
                        <p>
                            <i>GeoRank Points</i> are specific to the country ranking system. These points are aggregated from individual player points and reflect the overall strength of a country's player base. The points are calculated using a function based on the player's rank within their specific ranking type and game mode. GeoRank points allow the comparison of countries based on the performance of their top players.
                        </p>
                        <h3>5. Game Modes</h3>
                        <p>
                            <i>Game Modes</i> refer to different types of gameplay in GeoGuessr that determine how players earn their ratings and rankings. Each game mode focuses on a different aspect of the game and may reward performance in varying ways. Some common game modes include:
                        </p>
                        <ul>
                            <li><strong>Overall:</strong> A general ranking based on total performance, regardless of any specific conditions or gameplay rules.</li>
                            <li><strong>Moving:</strong> A ranking based on performance while moving, where players need to consider dynamic factors during the game.</li>
                            <li><strong>No Move:</strong> A ranking where players are restricted to stationary positions, testing their ability to perform in a fixed position.</li>
                            <li><strong>NMPZ (No Move, No PZ):</strong> A special mode where players have limitations on both movement and some other game mechanics, adding a layer of challenge to their performance.</li>
                        </ul>
                        <p>
                            Each game mode affects the rating system differently, with varying max rank thresholds and performance criteria. The ranking and points in each game mode are calculated separately, contributing to a diverse and nuanced player experience.
                        </p>

                        <p>
                            In summary, <i>Rating</i> determines a player's skill, <i>Rank</i> is their position relative to others, <i>Ranking</i> organizes players into different types, <i>GeoRank Points</i> are used to compare countries based on their players' performance, and <i>Game Modes</i> define how rankings are influenced by different gameplay conditions.
                        </p>
                    </AccordionTab>


                    <AccordionTab header="Why do points decrease sharply as rank increases?">
                        <p>
                            The logarithmic function ensures a <b>smooth curve</b> where <b>top players are rewarded significantly</b>, while 
                            lower-ranked players still receive meaningful points. This prevents sharp drop-offs and ensures fair distribution across rankings.
                        </p>
                    </AccordionTab>
                </Accordion>
            </div>
        </div>
    )
}